import { useMemo, useRef, useState } from 'react'

export interface NameOption {
  id: number
  name: string
  hint?: string
}

interface Props {
  value: string
  onChange: (name: string, id: number | null) => void
  options: NameOption[]
  placeholder?: string
  autoFocus?: boolean
}

// Free-text input with suggestions. Typing always keeps the raw text
// (id=null); picking an option links its id. Used for both patient and
// therapist fields in the appointment dialog.
export default function NameCombobox({ value, onChange, options, placeholder, autoFocus }: Props) {
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const blurTimer = useRef<ReturnType<typeof setTimeout>>()

  const query = value.trim().toLowerCase()
  const matches = useMemo(() => {
    const list = query
      ? options.filter((o) => o.name.toLowerCase().includes(query))
      : options
    return list.slice(0, 8)
  }, [options, query])

  function pick(opt: NameOption) {
    onChange(opt.name, opt.id)
    setOpen(false)
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        onChange={(e) => {
          onChange(e.target.value, null)
          setOpen(true)
          setHighlight(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setOpen(false), 150)
        }}
        onKeyDown={(e) => {
          if (!open || matches.length === 0) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlight((h) => Math.min(h + 1, matches.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlight((h) => Math.max(h - 1, 0))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            pick(matches[highlight]!)
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      {open && matches.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {matches.map((opt, i) => (
            <li
              key={opt.id}
              className={`px-3 py-2 cursor-pointer text-sm ${
                i === highlight ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50'
              }`}
              onMouseDown={(e) => {
                e.preventDefault() // beat the input blur
                pick(opt)
              }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span>{opt.name}</span>
              {opt.hint && <span className="ml-2 text-xs text-slate-400">{opt.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
